import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { licenseService } from '../services/api';
import { AssignOwnerModal, RevokeConfirmModal, DeactivateConfirmModal } from '../components/LicenseModals';

const LicenseDetail = () => {
  const { licenseId } = useParams();
  const navigate = useNavigate();
  const [license, setLicense] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Modals state
  const [showAssignOwnerModal, setShowAssignOwnerModal] = useState(false);
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showUnrevokeModal, setShowUnrevokeModal] = useState(false);

  const fetchLicense = useCallback(async () => {
    try {
      setLoading(true);
      const response = await licenseService.getById(licenseId);
      setLicense(response.license);
    } catch (err) {
      console.error('Error fetching license:', err);
      setError('Failed to load license details');
    } finally {
      setLoading(false);
    }
  }, [licenseId]);

  useEffect(() => {
    fetchLicense();
  }, [fetchLicense]);

  // Clear messages after 5 seconds
  useEffect(() => {
    if (message || error) {
      const timer = setTimeout(() => {
        setMessage(null);
        setError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message, error]);

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const formatRelativeDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const getStatusBadge = (status) => {
    const badges = {
      'unowned': { emoji: '🟡', text: 'Unowned', color: '#fff9e6', borderColor: '#ffe066' },
      'available': { emoji: '🟢', text: 'Available', color: '#e6f7ff', borderColor: '#91d5ff' },
      'activated': { emoji: '🟢', text: 'Activated', color: '#f0f9f0', borderColor: '#c3e6c3' },
      'deactivated': { emoji: '⚪', text: 'Deactivated', color: '#f5f5f5', borderColor: '#ddd' },
      'revoked': { emoji: '🔴', text: 'Revoked', color: '#ffe6e6', borderColor: '#ffcccc' },
    };
    const badge = badges[status] || badges['unowned'];
    return (
      <span className="status-badge" style={{ 
        background: badge.color, 
        border: `1px solid ${badge.borderColor}`,
        padding: '0.25rem 0.75rem',
        borderRadius: '12px',
        fontSize: '0.85rem'
      }}>
        {badge.emoji} {badge.text}
      </span>
    );
  };

  const getAssignmentMethodLabel = (method) => {
    const labels = {
      'manual': '👤 Manual Assignment',
      'auto': '⚡ Auto Assignment',
      'api': '🔌 API Assignment',
    };
    return labels[method] || method;
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!license) {
    return (
      <div className="error-page">
        <h2>License not found</h2>
        <p>The license you're looking for doesn't exist or has been deleted.</p>
        <Link to="/licenses" className="btn btn-primary">
          ← Back to Licenses
        </Link>
      </div>
    );
  }

  return (
    <div className="device-detail-page">
      {/* Page Header */}
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <Link to="/licenses" className="back-link">← Back to Licenses</Link>
          <h1 style={{ marginTop: '0.5rem', fontFamily: 'monospace', fontSize: '1.5rem' }}>
            {license.licenseKey}
          </h1>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem', alignItems: 'center' }}>
            {getStatusBadge(license.status)}
            {license.ownerType && (
              <span style={{ color: '#666' }}>
                {license.ownerType === 'tenant' ? '🏢' : '👤'} {license.ownerType === 'tenant' ? 'Tenant' : 'User'} License
              </span>
            )}
          </div>
        </div>
        <div className="header-actions" style={{ display: 'flex', gap: '0.5rem' }}>
          {!license.activatedAt && license.status !== 'revoked' && (
            <button 
              className="btn btn-primary" 
              onClick={() => setShowAssignOwnerModal(true)}
              disabled={actionLoading}
            >
              🔗 Assign Owner
            </button>
          )}
          {license.activatedAt && license.status !== 'deactivated' && (
            <button 
              className="btn btn-secondary" 
              onClick={() => setShowDeactivateModal(true)}
              disabled={actionLoading}
            >
              ⏸️ Deactivate
            </button>
          )}
          <div className="actions-dropdown">
            <button className="btn btn-secondary">Actions ▾</button>
            <div className="dropdown-menu">
              {license.status !== 'revoked' ? (
                <button 
                  className="dropdown-item text-danger" 
                  onClick={() => setShowRevokeModal(true)}
                >
                  ⛔ Revoke License
                </button>
              ) : (
                <button 
                  className="dropdown-item" 
                  onClick={() => setShowUnrevokeModal(true)}
                >
                  🔄 Unrevoke License
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {message && (
        <div className="alert alert-success">✅ {message}</div>
      )}
      {error && (
        <div className="alert alert-error">❌ {error}</div>
      )}

      {/* Detail Sections */}
      <div className="detail-sections">
        {/* License Information */}
        <div className="device-detail">
          <h2>🎫 License Information</h2>
          <div className="detail-grid">
            <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
              <label>License Key</label>
              <span style={{ 
                fontFamily: 'monospace', 
                fontSize: '1.1rem', 
                fontWeight: 'bold',
                padding: '0.5rem',
                background: '#f5f5f5',
                borderRadius: '4px',
                display: 'inline-block'
              }}>
                {license.licenseKey}
              </span>
            </div>
            <div className="detail-item">
              <label>Status</label>
              {getStatusBadge(license.status)}
            </div>
            <div className="detail-item">
              <label>License ID</label>
              <span style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{license.id}</span>
            </div>
            {license.ownerType && (
              <>
                <div className="detail-item">
                  <label>Owner Type</label>
                  <span style={{ textTransform: 'capitalize' }}>
                    {license.ownerType === 'tenant' ? '🏢 Tenant' : '👤 User'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Owner Name</label>
                  {license.ownerType === 'tenant' && license.ownerId ? (
                    <Link to={`/tenants/${license.ownerId}`} style={{ color: '#1976d2', textDecoration: 'none' }}>
                      {license.ownerName || 'Unknown'}
                    </Link>
                  ) : license.ownerType === 'user' && license.ownerId ? (
                    <Link to={`/users/${license.ownerId}`} style={{ color: '#1976d2', textDecoration: 'none' }}>
                      {license.ownerName || license.ownerEmail || 'Unknown'}
                    </Link>
                  ) : (
                    <span>{license.ownerName || license.ownerEmail || '—'}</span>
                  )}
                </div>
              </>
            )}
            <div className="detail-item">
              <label>Created Date</label>
              <span>{formatDate(license.createdAt)}</span>
            </div>
            <div className="detail-item">
              <label>Created By</label>
              <span>{license.createdBy?.name || license.createdBy?.email || '—'}</span>
            </div>
            {license.activatedAt && (
              <div className="detail-item">
                <label>Activated Date</label>
                <span>{formatDate(license.activatedAt)}</span>
              </div>
            )}
            {license.revokedAt && (
              <>
                <div className="detail-item">
                  <label>Revoked Date</label>
                  <span>{formatDate(license.revokedAt)}</span>
                </div>
                <div className="detail-item">
                  <label>Revoked By</label>
                  <span>{license.revokedBy?.name || license.revokedBy?.email || '—'}</span>
                </div>
                {license.revocationReason && (
                  <div className="detail-item" style={{ gridColumn: '1 / -1' }}>
                    <label>Revocation Reason</label>
                    <span>{license.revocationReason}</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        {/* Current Device (if activated) */}
        {license.activatedAt && license.device && (
          <div className="device-detail">
            <h2>📱 Current Device</h2>
            <div className="iot-status-box" style={{ 
              background: '#f0f9f0', 
              border: '1px solid #c3e6c3', 
              borderRadius: '8px', 
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                <span style={{ fontSize: '1.5rem' }}>📱</span>
                <div>
                  <Link 
                    to={`/ggos-devices/${license.device.thingName}`}
                    style={{ fontWeight: 'bold', fontSize: '1.1rem', color: '#1976d2', textDecoration: 'none' }}
                  >
                    {license.device.thingName}
                  </Link>
                  <span style={{ marginLeft: '0.5rem', color: '#666', fontSize: '0.85rem' }}>
                    (Device ID: {license.device.id})
                  </span>
                </div>
              </div>
              <div className="detail-grid">
                <div className="detail-item">
                  <label>IMEI</label>
                  <span style={{ fontFamily: 'monospace' }}>{license.device.imei || '—'}</span>
                </div>
                <div className="detail-item">
                  <label>Connection Status</label>
                  <span>
                    {license.device.connectionStatus === 'online' ? '🟢 Online' : '⚪ Offline'}
                  </span>
                </div>
                <div className="detail-item">
                  <label>Last Connected</label>
                  <span>{formatRelativeDate(license.device.lastConnectedAt)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Assignment History Timeline */}
        {license.history && license.history.length > 0 && (
          <div className="device-detail">
            <h2>📜 Assignment History</h2>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column', 
              gap: '1rem',
              position: 'relative',
              paddingLeft: '2rem'
            }}>
              {/* Timeline line */}
              <div style={{
                position: 'absolute',
                left: '0.75rem',
                top: '1rem',
                bottom: '1rem',
                width: '2px',
                background: '#ddd'
              }}></div>
              
              {license.history.map((entry, index) => (
                <div 
                  key={index}
                  style={{
                    position: 'relative',
                    padding: '1rem',
                    background: '#f9f9f9',
                    borderRadius: '8px',
                    border: '1px solid #e0e0e0'
                  }}
                >
                  {/* Timeline dot */}
                  <div style={{
                    position: 'absolute',
                    left: '-1.4rem',
                    top: '1.5rem',
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: entry.action === 'activated' ? '#4caf50' : 
                                entry.action === 'deactivated' ? '#999' : '#2196f3',
                    border: '2px solid white',
                    boxShadow: '0 0 0 2px #ddd'
                  }}></div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '0.25rem' }}>
                        {entry.unassignedAt ? '⚪ Device Deactivated' : '🟢 Device Activated'}
                      </div>
                      
                      {entry.deviceId && (
                        <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                          Device: <Link 
                            to={`/ggos-devices/${entry.deviceThingName}`}
                            style={{ color: '#1976d2', textDecoration: 'none' }}
                          >
                            {entry.deviceThingName || entry.deviceId}
                          </Link>
                          {entry.deviceImei && (
                            <span style={{ marginLeft: '0.5rem', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                              (IMEI: {entry.deviceImei})
                            </span>
                          )}
                        </div>
                      )}
                      
                      {entry.assignmentMethod && (
                        <div style={{ color: '#666', fontSize: '0.9rem', marginBottom: '0.25rem' }}>
                          Method: {getAssignmentMethodLabel(entry.assignmentMethod)}
                        </div>
                      )}
                      
                      {entry.assignedBy && (
                        <div style={{ color: '#666', fontSize: '0.9rem' }}>
                          By: {entry.assignedBy.name || entry.assignedBy.email}
                        </div>
                      )}
                    </div>
                    
                    <div style={{ 
                      textAlign: 'right', 
                      color: '#999', 
                      fontSize: '0.85rem',
                      minWidth: '140px'
                    }}>
                      <div>{formatDate(entry.assignedAt)}</div>
                      <div style={{ fontSize: '0.75rem', marginTop: '0.25rem' }}>
                        {formatRelativeDate(entry.assignedAt)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {license.history && license.history.length === 0 && (
          <div className="device-detail">
            <h2>📜 Assignment History</h2>
            <div className="inventory-box" style={{ 
              background: '#f9f9f9', 
              border: '1px solid #e0e0e0', 
              borderRadius: '8px', 
              padding: '1rem'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#999' }}>
                <span style={{ fontSize: '1.5rem' }}>📋</span>
                <div>
                  <strong>No History</strong>
                  <div style={{ fontSize: '0.85rem' }}>
                    This license has not been assigned or activated yet.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals */}
      {showAssignOwnerModal && (
        <AssignOwnerModal
          license={license}
          onClose={() => setShowAssignOwnerModal(false)}
          onSuccess={() => {
            setMessage('Owner assigned successfully');
            setShowAssignOwnerModal(false);
            fetchLicense();
          }}
          onError={(err) => setError(err)}
        />
      )}
      {showRevokeModal && (
        <RevokeConfirmModal
          license={license}
          onClose={() => setShowRevokeModal(false)}
          onSuccess={() => {
            setMessage('License revoked successfully');
            setShowRevokeModal(false);
            fetchLicense();
          }}
          onError={(err) => setError(err)}
        />
      )}
      {showDeactivateModal && (
        <DeactivateConfirmModal
          license={license}
          onClose={() => setShowDeactivateModal(false)}
          onSuccess={() => {
            setMessage('License deactivated successfully');
            setShowDeactivateModal(false);
            fetchLicense();
          }}
          onError={(err) => setError(err)}
        />
      )}
    </div>
  );
};

export default LicenseDetail;
